import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2, Clock, X, Home, Briefcase, Star } from 'lucide-react';
import axios from 'axios';

// Local storage key for recent addresses
const RECENT_ADDRESSES_KEY = 'jstyle_recent_addresses';
const MAX_RECENT_ADDRESSES = 10;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Get recent addresses from localStorage
 */
const getRecentAddresses = () => {
  try {
    const stored = localStorage.getItem(RECENT_ADDRESSES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

/**
 * Save address to recent addresses in localStorage
 */
const saveRecentAddress = (address) => {
  try {
    const recent = getRecentAddresses();
    // Remove if already exists (to move to top)
    const filtered = recent.filter(a => a.address !== address.address);
    // Add to beginning
    const updated = [address, ...filtered].slice(0, MAX_RECENT_ADDRESSES);
    localStorage.setItem(RECENT_ADDRESSES_KEY, JSON.stringify(updated));
    return updated;
  } catch (e) {
    console.error('Error saving recent address:', e);
    return [];
  }
};

/**
 * Sync address to backend for logged-in users
 */
const syncAddressToBackend = async (userId, addressData) => {
  if (!userId) return;
  try {
    await axios.post(`${BACKEND_URL}/api/users/${userId}/addresses`, addressData);
  } catch (e) {
    console.error('Error syncing address to backend:', e);
  }
};

/**
 * Fetch addresses from backend for logged-in users
 */
const fetchBackendAddresses = async (userId) => {
  if (!userId) return null;
  try {
    const response = await axios.get(`${BACKEND_URL}/api/users/${userId}/addresses`);
    return response.data.addresses || [];
  } catch (e) {
    console.error('Error fetching addresses from backend:', e);
    return null;
  }
};

/**
 * Get icon for address label
 */
const getLabelIcon = (label) => {
  if (!label) return Clock;
  const lower = label.toLowerCase();
  if (lower === 'home') return Home;
  if (lower === 'work') return Briefcase;
  if (lower === 'favorite') return Star;
  return Clock;
};

/**
 * AddressAutocomplete - A reusable address input with autocomplete suggestions
 * Uses OpenStreetMap Nominatim API (free, no API key required)
 * Also shows recently used addresses for quick selection
 * Syncs with backend for logged-in users
 * Includes quick-select buttons for Home and Work addresses
 */
const AddressAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "Enter address", 
  className = "",
  onSelect,
  id,
  showQuickSelect = true,  // Show Home/Work quick buttons
  "data-testid": dataTestId
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [recentAddresses, setRecentAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [showRecent, setShowRecent] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(null); // 'home' or 'work'
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  
  // Get user ID for backend sync
  const userId = localStorage.getItem('user_id');

  // Get labeled addresses
  const homeAddress = recentAddresses.find(a => a.label?.toLowerCase() === 'home');
  const workAddress = recentAddresses.find(a => a.label?.toLowerCase() === 'work');

  // Load recent addresses on mount (from backend if logged in, else localStorage)
  useEffect(() => {
    const loadAddresses = async () => {
      if (userId) {
        const backendAddresses = await fetchBackendAddresses(userId);
        if (backendAddresses && backendAddresses.length > 0) {
          setRecentAddresses(backendAddresses);
          // Also update localStorage for offline access
          localStorage.setItem(RECENT_ADDRESSES_KEY, JSON.stringify(backendAddresses));
          return;
        }
      }
      // Fallback to localStorage
      setRecentAddresses(getRecentAddresses());
    };
    loadAddresses();
  }, [userId]);

  // Quick select Home or Work
  const handleQuickSelect = (label) => {
    const address = label === 'home' ? homeAddress : workAddress;
    if (address) {
      setInputValue(address.address);
      onChange(address.address);
      setShowSuggestions(false);
      setShowRecent(false);
      if (onSelect) {
        onSelect(address);
      }
    } else {
      // Show prompt to set this address
      setShowLabelModal(label);
    }
  };

  // Save current address with label
  const saveAddressWithLabel = async (label) => {
    if (!inputValue) {
      setShowLabelModal(null);
      return;
    }
    
    const addressData = {
      address: inputValue,
      label: label.charAt(0).toUpperCase() + label.slice(1), // Capitalize
      city: '',
      state: ''
    };
    
    // Update in recent addresses
    const existing = recentAddresses.find(a => a.address === inputValue);
    let updated;
    if (existing) {
      // Update existing address with label
      updated = recentAddresses.map(a => 
        a.address === inputValue ? { ...a, label: addressData.label } : a
      );
    } else {
      // Add new address with label
      updated = [addressData, ...recentAddresses.filter(a => a.label?.toLowerCase() !== label)].slice(0, MAX_RECENT_ADDRESSES);
    }
    
    // Remove any other address with same label
    updated = updated.map(a => {
      if (a.address !== inputValue && a.label?.toLowerCase() === label) {
        return { ...a, label: null };
      }
      return a;
    });
    
    setRecentAddresses(updated);
    localStorage.setItem(RECENT_ADDRESSES_KEY, JSON.stringify(updated));
    
    // Sync to backend
    if (userId) {
      try {
        await axios.put(`${BACKEND_URL}/api/users/${userId}/addresses/label`, {
          address: inputValue,
          label: addressData.label
        });
      } catch (e) {
        console.error('Error saving label:', e);
      }
    }
    
    setShowLabelModal(null);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setShowRecent(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync with external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  // Debounced search function
  const searchAddress = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setShowRecent(true);
      return;
    }

    setShowRecent(false);
    setIsLoading(true);
    try {
      // Using OpenStreetMap Nominatim API (free)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=us&limit=5&addressdetails=1`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'JStyleServices/1.0'
          }
        }
      );
      
      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      const formattedSuggestions = data.map(item => ({
        id: item.place_id,
        address: item.display_name,
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        city: item.address?.city || item.address?.town || item.address?.village || '',
        state: item.address?.state || '',
        shortAddress: formatShortAddress(item)
      }));
      
      setSuggestions(formattedSuggestions);
      setShowSuggestions(formattedSuggestions.length > 0);
    } catch (error) {
      console.error('Address search error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Format a shorter, cleaner address
  const formatShortAddress = (item) => {
    const parts = [];
    if (item.address?.house_number) parts.push(item.address.house_number);
    if (item.address?.road) parts.push(item.address.road);
    if (item.address?.city || item.address?.town || item.address?.village) {
      parts.push(item.address.city || item.address.town || item.address.village);
    }
    if (item.address?.state) parts.push(item.address.state);
    return parts.join(', ') || item.display_name.split(',').slice(0, 3).join(',');
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);

    // Debounce the search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchAddress(newValue);
    }, 300);
  };

  // Handle input focus - show recent addresses
  const handleFocus = () => {
    if (!inputValue || inputValue.length < 3) {
      setShowRecent(true);
      setShowSuggestions(false);
    } else if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    const addressData = {
      address: suggestion.shortAddress || suggestion.address,
      full_address: suggestion.address || suggestion.fullAddress,
      lat: suggestion.lat,
      lng: suggestion.lng,
      city: suggestion.city,
      state: suggestion.state
    };
    
    setInputValue(addressData.address);
    onChange(addressData.address);
    setShowSuggestions(false);
    setShowRecent(false);
    setSuggestions([]);
    
    // Save to recent addresses (localStorage)
    const updated = saveRecentAddress(addressData);
    setRecentAddresses(updated);
    
    // Sync to backend for logged-in users
    if (userId) {
      syncAddressToBackend(userId, addressData);
    }
    
    if (onSelect) {
      onSelect(addressData);
    }
  };

  // Handle recent address selection
  const handleSelectRecent = (recentAddr) => {
    setInputValue(recentAddr.address);
    onChange(recentAddr.address);
    setShowRecent(false);
    
    if (onSelect) {
      onSelect(recentAddr);
    }
  };

  // Clear a recent address
  const handleClearRecent = (e, addressToRemove) => {
    e.stopPropagation();
    try {
      const recent = getRecentAddresses();
      const updated = recent.filter(a => a.address !== addressToRemove);
      localStorage.setItem(RECENT_ADDRESSES_KEY, JSON.stringify(updated));
      setRecentAddresses(updated);
    } catch (err) {
      console.error('Error clearing recent address:', err);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      {/* Quick Select Buttons for Home and Work */}
      {showQuickSelect && (
        <div className="flex gap-2 mb-2">
          <button
            type="button"
            onClick={() => handleQuickSelect('home')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              homeAddress 
                ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200 border-dashed'
            }`}
            data-testid="quick-select-home"
            title={homeAddress ? homeAddress.address : 'Set Home address'}
          >
            <Home size={14} />
            <span>Home</span>
            {homeAddress && <span className="text-xs opacity-70">✓</span>}
          </button>
          <button
            type="button"
            onClick={() => handleQuickSelect('work')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              workAddress 
                ? 'bg-blue-100 text-blue-800 hover:bg-blue-200 border border-blue-300' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200 border-dashed'
            }`}
            data-testid="quick-select-work"
            title={workAddress ? workAddress.address : 'Set Work address'}
          >
            <Briefcase size={14} />
            <span>Work</span>
            {workAddress && <span className="text-xs opacity-70">✓</span>}
          </button>
        </div>
      )}

      {/* Label Modal - Set address as Home/Work */}
      {showLabelModal && (
        <div className="absolute z-[60] top-0 left-0 right-0 bg-white border-2 border-amber-400 rounded-lg shadow-xl p-4 animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center gap-2 mb-3">
            {showLabelModal === 'home' ? (
              <Home size={20} className="text-amber-600" />
            ) : (
              <Briefcase size={20} className="text-blue-600" />
            )}
            <h4 className="font-semibold text-gray-900">
              Set as {showLabelModal === 'home' ? 'Home' : 'Work'}
            </h4>
          </div>
          {inputValue ? (
            <>
              <p className="text-sm text-gray-600 mb-3">
                Save "<span className="font-medium">{inputValue}</span>" as your {showLabelModal} address?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => saveAddressWithLabel(showLabelModal)}
                  className="flex-1 px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
                >
                  Save as {showLabelModal === 'home' ? 'Home' : 'Work'}
                </button>
                <button
                  onClick={() => setShowLabelModal(null)}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-3">
                Enter an address first, then click {showLabelModal === 'home' ? 'Home' : 'Work'} to save it.
              </p>
              <button
                onClick={() => setShowLabelModal(null)}
                className="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
              >
                Got it
              </button>
            </>
          )}
        </div>
      )}

      <div className="relative">
        <Input
          id={id}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={(e) => {
            // Allow Enter key to confirm manual address entry
            if (e.key === 'Enter') {
              e.preventDefault();
              setShowSuggestions(false);
              setShowRecent(false);
              // Trigger onSelect with the manually typed address
              if (inputValue && onSelect) {
                onSelect({ address: inputValue, manual: true });
              }
            }
            // Allow Escape to close dropdown
            if (e.key === 'Escape') {
              setShowSuggestions(false);
              setShowRecent(false);
            }
          }}
          onBlur={() => {
            // Small delay to allow click on suggestions
            setTimeout(() => {
              setShowSuggestions(false);
              setShowRecent(false);
            }, 200);
          }}
          placeholder={placeholder}
          className={`pr-10 ${className}`}
          autoComplete="off"
          data-testid={dataTestId}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isLoading ? (
            <Loader2 size={16} className="text-gray-400 animate-spin" />
          ) : (
            <MapPin size={16} className="text-gray-400" />
          )}
        </div>
      </div>
      
      {/* Recent Addresses dropdown */}
      {showRecent && recentAddresses.length > 0 && !showSuggestions && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
          <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase border-b bg-gray-50">
            <Clock size={12} className="inline mr-1" />
            Recent Addresses
          </div>
          {recentAddresses.map((recent, index) => {
            const LabelIcon = getLabelIcon(recent.label);
            const isLabeled = recent.label && ['home', 'work', 'favorite'].includes(recent.label.toLowerCase());
            return (
              <button
                key={`recent-${index}`}
                type="button"
                onClick={() => handleSelectRecent(recent)}
                className="w-full px-4 py-3 text-left hover:bg-amber-50 border-b border-gray-100 last:border-b-0 transition-colors group"
                data-testid={`recent-${index}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isLabeled ? 'bg-amber-100' : 'bg-gray-100'
                  }`}>
                    <LabelIcon size={16} className={isLabeled ? 'text-amber-600' : 'text-gray-500'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 truncate">{recent.address}</p>
                      {recent.label && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                          {recent.label}
                        </span>
                      )}
                    </div>
                    {recent.city && (
                      <p className="text-xs text-gray-500">{recent.city}, {recent.state}</p>
                    )}
                  </div>
                  <button
                    onClick={(e) => handleClearRecent(e, recent.address)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-all"
                    title="Remove from recent"
                  >
                    <X size={14} className="text-gray-400" />
                  </button>
                </div>
              </button>
            );
          })}
        </div>
      )}
      
      {/* API Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-amber-50 border-b border-gray-100 last:border-b-0 transition-colors"
              data-testid={`suggestion-${suggestion.id}`}
            >
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{suggestion.shortAddress}</p>
                  <p className="text-xs text-gray-500 truncate">{suggestion.address}</p>
                </div>
              </div>
            </button>
          ))}
          {/* Clickable button to use typed address */}
          <button
            type="button"
            onClick={() => {
              setShowSuggestions(false);
              setShowRecent(false);
              if (inputValue && onSelect) {
                onSelect({ address: inputValue, manual: true });
              }
            }}
            className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold text-sm transition-all border-t-2 border-green-400"
            data-testid="use-typed-address-btn"
          >
            <div className="flex items-center justify-center gap-2">
              <span className="animate-pulse">👆</span>
              <span>Please TAP HERE to use typed address for better results</span>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
